import { Button } from "@/components/Primitives/Button"
import { Icon } from "@/components/Primitives/Icon"
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "@/components/Primitives/Modal"
import { Text } from "@/components/Primitives/Text"
import { useConfig } from "@/hooks/useConfig"
import { useNavigate } from "react-router-dom"
import { BookOpen, ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { useCircleStore } from "@/lib/store/circle"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { getQuestImageUrl } from "@/hooks/useQuests"
import { Quest } from "@/types/quests"

interface QuestModalProps {
  isOpen: boolean
  onClose: () => void
  quest: Quest
}

export function QuestModal({ isOpen, onClose, quest }: QuestModalProps) {
  const [showInfo, setShowInfo] = useState(true)
  const { t, tp } = useConfig()
  const navigate = useNavigate()
  const { selectedCircleId } = useCircleStore()

  const handleContinue = () => {
    if (!selectedCircleId) {
      toast.error(
        t("config_error_no_circle_selected") ||
          "No circle selected. Please select a circle before starting the quest.",
      )
      return
    }
    if (!quest.id) {
      toast.error(
        t("config_error_no_quest_id") || "No quest ID found. Please try again.",
      )
      return
    }

    navigate(`/circle/${selectedCircleId}/questions?quest_id=${quest.id}`)
  }

  // New quests won't have stages so preview doesn't make a big deal out of them.
  const stagesContent =
    quest.stages.length > 1
      ? (quest.stages || []).map((stage, index: number) => (
          <div key={index} className="pb-2 first:pt-0 last:pb-0">
            <div className="flex items-start gap-4">
              <div className="pt-1">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Icon icon={BookOpen} size="sm" className="text-purple-500" />
                </div>
              </div>
              <div>
                <Text variant="caption" bold className="text-neutral-600">
                  {tp("config_header_stage", { index: `${index + 1}` })}
                </Text>
                <Text variant="paragraph" bold>
                  {stage.title}
                </Text>
                <MarkdownRenderer content={stage.summary} />
              </div>
            </div>
          </div>
        ))
      : null

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="p-0">
        <div className="flex flex-col">
          <div className="absolute top-4 right-4 z-10">
            <ModalClose className="text-neutral-50" />
          </div>
          <div className="relative w-full h-[12rem] md:h-[19rem] bg-muted">
            <img
              src={getQuestImageUrl(quest.image_url)}
              alt={quest.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <ModalHeader>
          <div className="flex justify-between items-center px-4">
            <ModalTitle className="text-text-foreground text-left">
              {quest.title}
            </ModalTitle>
            <button
              className="hover:opacity-70"
              onClick={() => setShowInfo(!showInfo)}
            >
              <Icon icon={showInfo ? ChevronUp : ChevronDown} size="sm" />
            </button>
          </div>
          {showInfo && (
            <div className="px-4 space-y-2 ">
              <MarkdownRenderer content={quest.summary} />
            </div>
          )}
        </ModalHeader>
        <div className="p-4 pb-0 space-y-6">
          <div className="space-y-4 mb-0">
            <div className="space-y-2 overflow-x-scroll max-h-[12.5rem]">
              {stagesContent}
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t p-4">
          <Button onClick={handleContinue}>
            {t("config_button_continue")}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  )
}
